import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckCompartmentJSON {
  type: GeckoFeatureModelType.ShipDeckCompartment
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
}

export const NewGeckoShipDeckCompartmentJSON: GeckoShipDeckCompartmentJSON = {
  type: GeckoFeatureModelType.ShipDeckCompartment,
  name: 'Compartment',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ],
  },
}

export class GeckoShipDeckCompartment extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckCompartment
  readonly geometry: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]

  constructor(
    id: string,
    name: string,
    geometry: { x: GeckoMeasurement; y: GeckoMeasurement }[]
  ) {
    super(id)
    this.name = name
    if (geometry.length < 3) {
      throw new Error(
        'Ship Deck Compartments must have at least 3 points in their geometry'
      )
    }
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    compartmentID: string,
    compartmentJSON: GeckoShipDeckCompartmentJSON
  ): GeckoShipDeckCompartment {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const featureGeometryData = {
      coordinates: compartmentJSON.feature_geometry.coordinates.map(
        (coord) => ({
          x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
          y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
        })
      ),
    }

    const compartmentName = compartmentJSON.name as string

    return new GeckoShipDeckCompartment(
      compartmentID,
      compartmentName,
      featureGeometryData.coordinates
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckCompartmentJSON>
  ): Map<string, GeckoShipDeckCompartment> {
    return Object.keys(recordsJSON).reduce((resultObj, compartmentID) => {
      resultObj.set(
        compartmentID,
        GeckoShipDeckCompartment.CreateFromJSON(
          componentID,
          compartmentID,
          recordsJSON[compartmentID]
        )
      )
      return resultObj
    }, new Map<string, GeckoShipDeckCompartment>())
  }

  asJSON() {
    return generateCompartment(this.name, this.geometry)
  }
}

export function generateCompartment(
  name: string,
  geometry: { x: GeckoMeasurement; y: GeckoMeasurement }[]
): GeckoShipDeckCompartmentJSON {
  const compartmentJSON: GeckoShipDeckCompartmentJSON = {
    type: GeckoFeatureModelType.ShipDeckCompartment,
    name: name,
    feature_geometry: {
      coordinates: geometry.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
    },
  }

  return compartmentJSON
}

export const ShipDeckCompartmentFormFields = (
  compartmentJSON: GeckoShipDeckCompartmentJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: compartmentJSON.name,
      unit_magnitude: null,
      initialValue: 'Compartment',
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: compartmentJSON.feature_geometry.coordinates
        .map((coordinate) => `[${coordinate.x}, ${coordinate.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(3),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckCompartmentJSONFromFormFields = (
  formFields: FormField[]
) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string

  return generateCompartment(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    }))
  )
}

export const ShipDeckCompartmentFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckCompartment,
  title: 'Compartments',
  singularName: 'Compartment',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to create a Ship Deck Compartment, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a>.',
}
